import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-hot-toast';
import SearchableSelect from './SearchableSelect';
import { authHeaders } from '../utils/apiAuth';
import { errorText } from '../utils/apiErrors';
import { getVatDetails } from '../utils/vat';
import { CAP } from '../utils/permissions';
import { usePermissions } from '../utils/PermissionContext';

const formatDateToInput = (dateObj) => {
  if (!dateObj || isNaN(dateObj.getTime())) return '';
  const yyyy = dateObj.getFullYear();
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const dd = String(dateObj.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const formatInputDateToDisplay = (yyyyMmDd) => {
  if (!yyyyMmDd) return '';
  const parts = yyyyMmDd.split('-');
  if (parts.length !== 3) return yyyyMmDd;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const parseDdMmYyyy = (str) => {
  if (!str || typeof str !== 'string') return null;
  const trimmed = str.trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
    const [dd, mm, yyyy] = trimmed.split('/').map(Number);
    return new Date(yyyy, mm - 1, dd);
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const [yyyy, mm, dd] = trimmed.slice(0, 10).split('-').map(Number);
    return new Date(yyyy, mm - 1, dd);
  }
  return null;
};

const addOneYearInputDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return '';
  const [yyyy, mm, dd] = yyyyMmDd.split('-').map(Number);
  if (!yyyy || !mm || !dd) return '';
  const nextYearDate = new Date(yyyy + 1, mm - 1, dd);
  return formatDateToInput(nextYearDate);
};

const subtractOneYearInputDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return '';
  const [yyyy, mm, dd] = yyyyMmDd.split('-').map(Number);
  if (!yyyy || !mm || !dd) return '';
  const prevYearDate = new Date(yyyy - 1, mm - 1, dd);
  return formatDateToInput(prevYearDate);
};

const getDefaultDates = () => {
  const today = new Date();
  const start = formatDateToInput(today);
  const end = addOneYearInputDate(start);
  return { start, end };
};

const createBlankItem = () => {
  const { start, end } = getDefaultDates();
  return {
    productId: '',
    subscriptionId: '',
    name: '',
    skuId: '',
    planName: 'ANNUAL_YEARLY_PAY',
    quantity: 1,
    price: 0,
    afasArticleCode: '',
    startDate: start,
    endDate: end,
    isRenewal: true,
  };
};

const CreateOrderModal = ({ isOpen, onClose, onSuccess, initialCustomerId = '', t = {} }) => {
  const { can } = usePermissions();
  const canWrite = can(CAP.ADMIN_WRITE);
  const apiUrl = process.env.REACT_APP_API_URL || '';

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(false);

  const [selectedCustomerId, setSelectedCustomerId] = useState(initialCustomerId || '');
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);

  const [items, setItems] = useState([createBlankItem()]);
  const [purchaseOrder, setPurchaseOrder] = useState('Domain Reactivation');
  const [skipResellerApi, setSkipResellerApi] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load customers and product catalog when modal opens
  useEffect(() => {
    if (!isOpen || !canWrite) return;

    let isMounted = true;
    const loadData = async () => {
      setLoadingInitial(true);
      try {
        const [custRes, wsRes] = await Promise.all([
          fetch(`${apiUrl}/api/customers`, { headers: authHeaders() }),
          fetch(`${apiUrl}/api/workspaces`, { headers: authHeaders() }),
        ]);

        if (custRes.ok && isMounted) {
          const custData = await custRes.json();
          setCustomers(Array.isArray(custData) ? custData : []);
        }

        const annualProducts = [];
        if (wsRes.ok) {
          const wsData = await wsRes.json();
          if (Array.isArray(wsData)) {
            wsData.forEach((p) => {
              if (parseFloat(p.price || 0) > 0) {
                annualProducts.push({ ...p, _source: 'workspaces' });
              }
            });
          }
        }
        if (isMounted) {
          setProducts(annualProducts);
        }
      } catch (err) {
        console.error('Error loading initial data for CreateOrderModal:', err);
      } finally {
        if (isMounted) setLoadingInitial(false);
      }
    };

    loadData();
    return () => {
      isMounted = false;
    };
  }, [isOpen, canWrite, apiUrl]);

  // Reset or initialize state when modal opens
  useEffect(() => {
    if (isOpen) {
      let defaultCustId = initialCustomerId;
      if (!defaultCustId) {
        try {
          const stored = localStorage.getItem('selectedCustomer');
          if (stored) {
            const parsed = JSON.parse(stored);
            defaultCustId = parsed?.customerId || parsed?.id || '';
          }
        } catch (e) {
          // ignore
        }
      }
      setSelectedCustomerId(defaultCustId || '');
      setItems([createBlankItem()]);
      setPurchaseOrder('Domain Reactivation');
      setSkipResellerApi(false);
    }
  }, [isOpen, initialCustomerId]);

  const selectedCustomer = useMemo(() => {
    if (!selectedCustomerId) return null;
    return customers.find((c) => (c.customerId || c.id) === selectedCustomerId) || null;
  }, [customers, selectedCustomerId]);

  // Fetch customer's current subscriptions when selectedCustomerId changes
  const fetchCustomerSubscriptions = useCallback(
    async (custId, fallbackSubs = []) => {
      if (!custId) {
        setSubscriptions([]);
        return;
      }
      setLoadingSubscriptions(true);
      try {
        const res = await fetch(`${apiUrl}/api/customers/${encodeURIComponent(custId)}/licenses`, {
          headers: authHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setSubscriptions(data);
            return;
          }
        }
        setSubscriptions(Array.isArray(fallbackSubs) ? fallbackSubs : []);
      } catch (err) {
        console.error('Failed to fetch customer subscriptions:', err);
        setSubscriptions(Array.isArray(fallbackSubs) ? fallbackSubs : []);
      } finally {
        setLoadingSubscriptions(false);
      }
    },
    [apiUrl]
  );

  useEffect(() => {
    if (!isOpen || !selectedCustomerId) {
      setSubscriptions([]);
      return;
    }
    const cust = customers.find((c) => (c.customerId || c.id) === selectedCustomerId);
    fetchCustomerSubscriptions(selectedCustomerId, cust?.subscriptions || []);
  }, [isOpen, selectedCustomerId, customers, fetchCustomerSubscriptions]);

  const customerOptions = useMemo(() => {
    return customers.map((c) => {
      const id = c.customerId || c.id || '';
      const domain = c.customerDomain || '';
      const name = c.customerName || c.name || '';
      const labelParts = [domain, name && name !== domain ? `(${name})` : '', `[${id}]`].filter(Boolean);
      return {
        value: id,
        label: labelParts.join(' '),
      };
    });
  }, [customers]);

  const productOptions = useMemo(() => {
    return products.map((p) => {
      const priceNum = parseFloat(p.price || 0);
      const sku = p.related_licenses_sku || p.skuId || '';
      const afas = p.afas_artikelcode || p.afasArticleCode || '';
      return {
        value: p.id,
        label: `${p.name} — €${priceNum.toFixed(2)}${afas ? ` [${afas}]` : ''}${sku ? ` (${sku})` : ''}`,
      };
    });
  }, [products]);

  const findMatchingCatalogProduct = useCallback(
    (skuId, skuName) => {
      if (skuId) {
        const bySku = products.find((p) => (p.related_licenses_sku || p.skuId) === skuId);
        if (bySku) return bySku;
      }
      if (skuName) {
        const lower = skuName.toLowerCase().trim();
        const byName = products.find((p) => (p.name || '').toLowerCase().trim() === lower);
        if (byName) return byName;
      }
      return null;
    },
    [products]
  );

  // Compute smart default startDate & endDate from a subscription
  const computeSubscriptionDateRange = (sub) => {
    const parsedRenewal = parseDdMmYyyy(sub.renewalDate);
    const parsedStart = parseDdMmYyyy(sub.startDate);

    if (parsedRenewal) {
      const today = new Date();
      const diffDays = Math.round((parsedRenewal - today) / (1000 * 60 * 60 * 24));
      const renewalInput = formatDateToInput(parsedRenewal);

      // If Google already rolled the commitment end date nearly a year ahead (> 60 days from now),
      // the current commitment period is [renewalDate - 1 year (or startDate), renewalDate].
      if (diffDays > 60) {
        const startInput = parsedStart
          ? formatDateToInput(parsedStart)
          : subtractOneYearInputDate(renewalInput);
        return { start: startInput, end: renewalInput };
      }

      // Otherwise, the renewal extends from renewalDate for +1 year
      return {
        start: renewalInput,
        end: addOneYearInputDate(renewalInput),
      };
    }

    return getDefaultDates();
  };

  const isAnnualSubscription = (sub) =>
    Boolean(sub?.planName && String(sub.planName).toUpperCase().includes('ANNUAL'));

  const handleSelectSubscription = (sub) => {
    if (!isAnnualSubscription(sub)) return;

    const matchedProd = findMatchingCatalogProduct(sub.skuId, sub.skuName);
    const { start, end } = computeSubscriptionDateRange(sub);
    const seats =
      sub.purchasedLicenses !== undefined && sub.purchasedLicenses !== null && sub.purchasedLicenses > 0
        ? sub.purchasedLicenses
        : sub.assignedLicenses || 1;

    const newItem = {
      productId: matchedProd ? matchedProd.id : sub.subscriptionId || 'manual_item',
      subscriptionId: sub.subscriptionId || '',
      name: matchedProd ? matchedProd.name : sub.skuName || 'Google Workspace License',
      skuId: sub.skuId || matchedProd?.related_licenses_sku || '',
      planName: 'ANNUAL_YEARLY_PAY',
      quantity: seats,
      price: matchedProd ? parseFloat(matchedProd.price || 0) : 0,
      afasArticleCode: matchedProd?.afas_artikelcode || matchedProd?.afasArticleCode || '',
      startDate: start,
      endDate: end,
      isRenewal: true,
    };

    setItems((prev) => {
      // If the first item is still blank, replace it; otherwise append or update existing subscription
      if (prev.length === 1 && !prev[0].name && !prev[0].productId) {
        return [newItem];
      }
      const existingIdx = prev.findIndex(
        (it) =>
          (sub.subscriptionId && it.subscriptionId === sub.subscriptionId) ||
          (sub.skuId && it.skuId === sub.skuId)
      );
      if (existingIdx >= 0) {
        const copy = [...prev];
        copy[existingIdx] = newItem;
        return copy;
      }
      return [...prev, newItem];
    });

    toast.success(
      `${sub.skuName || 'Subscription'} ${t.subscription_applied || 'loaded into order form'}`
    );
  };

  const handleProductChange = (index, productId) => {
    const prod = products.find((p) => p.id === productId);
    if (!prod) return;

    setItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        productId: prod.id,
        name: prod.name || '',
        skuId: prod.related_licenses_sku || prod.skuId || '',
        price: parseFloat(prod.price || 0),
        afasArticleCode: prod.afas_artikelcode || prod.afasArticleCode || '',
      };
      return next;
    });
  };

  const handleItemFieldChange = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };

      // Automatically extend end date by 1 year when startDate changes on Annual plans
      if (field === 'startDate' && value && updated.planName !== 'FLEXIBLE') {
        updated.endDate = addOneYearInputDate(value);
      }
      next[index] = updated;
      return next;
    });
  };

  const handleAddLineItem = () => {
    setItems((prev) => [...prev, createBlankItem()]);
  };

  const handleRemoveLineItem = (index) => {
    setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const { vatRate, vatPercent } = useMemo(
    () => getVatDetails(selectedCustomer),
    [selectedCustomer]
  );

  const subtotal = useMemo(() => {
    return items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const pr = parseFloat(item.price) || 0;
      return sum + qty * pr;
    }, 0);
  }, [items]);

  const vatAmount = useMemo(() => subtotal * vatRate, [subtotal, vatRate]);
  const totalPrice = useMemo(() => subtotal + vatAmount, [subtotal, vatAmount]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCustomerId) {
      toast.error(t.choose_customer || 'Please select a customer.');
      return;
    }

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!it.name || !it.name.trim()) {
        toast.error(t.missing_item_name || `Line ${i + 1}: Please select or enter a product name.`);
        return;
      }
      if (!it.quantity || Number(it.quantity) <= 0) {
        toast.error(`Line ${i + 1}: Quantity must be at least 1.`);
        return;
      }
      if (it.price === '' || Number(it.price) <= 0) {
        toast.error(`Line ${i + 1}: Unit price must be greater than €0.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        customerId: selectedCustomerId,
        orderUser: 'Admin (Manual Reactivation)',
        purchaseOrder: purchaseOrder.trim() || 'Domain Reactivation',
        status: 'Pending Payment',
        paymentMethod: 'Stripe',
        skipResellerApi: Boolean(skipResellerApi),
        cartItems: items.map((it) => {
          const startFormatted = formatInputDateToDisplay(it.startDate);
          const endFormatted = formatInputDateToDisplay(it.endDate);
          const subtitle =
            startFormatted && endFormatted
              ? `${it.name.trim()} - ${startFormatted} - ${endFormatted}`
              : it.name.trim();

          return {
            id: it.productId || it.subscriptionId || 'manual_item',
            subscriptionId: it.subscriptionId || undefined,
            name: it.name.trim(),
            item_subtitle: subtitle,
            type: 'license',
            skuId: it.skuId.trim(),
            planName: it.planName || 'ANNUAL_YEARLY_PAY',
            quantity: Number(it.quantity),
            price: Number(it.price),
            afasArticleCode: it.afasArticleCode.trim(),
            isRenewal: Boolean(it.isRenewal),
            startDate: it.startDate,
            endDate: it.endDate,
            renewalDate: it.startDate ? `${it.startDate}T00:00:00Z` : undefined,
          };
        }),
      };

      const res = await fetch(`${apiUrl}/api/orders/create-missing`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(errorText(data, t, 'Failed to create order'));
      }

      toast.success(
        `${t.create_order_success || 'Order created successfully'}: ${data.orderId}`
      );
      if (onSuccess) {
        onSuccess(data);
      }
      onClose();
    } catch (err) {
      console.error('Error creating missing order:', err);
      toast.error(err.message || 'Failed to create order');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !canWrite) return null;

  return createPortal(
    <div
      className="fixed inset-0 !m-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-xs p-4"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-4 bg-primary text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <div>
              <h3 className="text-lg font-bold leading-tight">
                {t.create_order || 'Create Order'}
              </h3>
              <p className="text-xs text-purple-100">
                {t.create_order_subtitle ||
                  'Create a missing subscription order so the customer can pay via Stripe.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* 1. Customer Selection */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-2">
              {t.customer_selection || '1. Select Customer'} *
            </label>
            {loadingInitial ? (
              <div className="h-10 bg-gray-100 animate-pulse rounded-lg" />
            ) : (
              <SearchableSelect
                options={customerOptions}
                value={selectedCustomerId}
                onChange={(val) => {
                  if (val !== selectedCustomerId) {
                    setSelectedCustomerId(val);
                    setItems([createBlankItem()]);
                  }
                }}
                placeholder={t.choose_customer || 'Search customer by domain, name, or ID...'}
              />
            )}
            {selectedCustomer && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                <span className="px-2 py-0.5 bg-purple-50 text-primary font-semibold rounded border border-purple-200">
                  {selectedCustomer.customerDomain}
                </span>
                {selectedCustomer.administratie && (
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded border border-gray-200">
                    {selectedCustomer.administratie}
                  </span>
                )}
                {selectedCustomer.afasDebtorNum && (
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-200">
                    Debtor #{selectedCustomer.afasDebtorNum}
                  </span>
                )}
                <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded border border-emerald-200">
                  {t.vat || 'VAT'}: {vatPercent}%
                </span>
              </div>
            )}
          </div>

          {/* 2. Customer Current Subscriptions (Auto-fetched) */}
          {selectedCustomerId && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 text-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  {t.current_customer_subscriptions ||
                    'Customer Current Subscriptions (Click to fill order)'}
                </h4>
                {loadingSubscriptions && (
                  <span className="text-xs text-primary font-medium animate-pulse">
                    {t.loading || 'Loading...'}
                  </span>
                )}
              </div>

              {loadingSubscriptions ? (
                <div className="space-y-2">
                  <div className="h-12 bg-gray-200/70 animate-pulse rounded-lg" />
                  <div className="h-12 bg-gray-200/70 animate-pulse rounded-lg" />
                </div>
              ) : subscriptions.length === 0 ? (
                <p className="text-xs text-gray-500 italic">
                  {t.no_subscriptions_found ||
                    'No active subscriptions found for this customer. You can select a product manually below.'}
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto pr-1">
                  {subscriptions.map((sub, idx) => {
                    const isAnnual = isAnnualSubscription(sub);
                    const matchedProd = findMatchingCatalogProduct(sub.skuId, sub.skuName);
                    const catalogPrice = matchedProd ? parseFloat(matchedProd.price || 0) : null;
                    const isSelectedInForm =
                      isAnnual &&
                      items.some(
                        (it) =>
                          (sub.subscriptionId && it.subscriptionId === sub.subscriptionId) ||
                          (sub.skuId && it.skuId === sub.skuId)
                      );

                    return (
                      <div
                        key={sub.subscriptionId || idx}
                        onClick={() => isAnnual && handleSelectSubscription(sub)}
                        className={`p-3 rounded-lg border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-2 ${
                          !isAnnual
                            ? 'bg-gray-100 border-gray-200 opacity-55 cursor-not-allowed select-none'
                            : isSelectedInForm
                              ? 'bg-purple-50/90 border-primary shadow-xs cursor-pointer'
                              : 'bg-white border-gray-200 hover:border-primary/50 hover:bg-gray-50 cursor-pointer'
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className={`font-bold text-sm ${
                                isAnnual ? 'text-gray-900' : 'text-gray-500'
                              }`}
                            >
                              {sub.skuName || sub.skuId}
                            </span>
                            {sub.status && (
                              <span
                                className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${
                                  !isAnnual
                                    ? 'bg-gray-200 text-gray-500'
                                    : sub.status === 'ACTIVE'
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {sub.status}
                              </span>
                            )}
                            {sub.planName && (
                              <span
                                className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded border ${
                                  isAnnual
                                    ? 'bg-gray-100 text-gray-700 border-gray-200'
                                    : 'bg-gray-200 text-gray-500 border-gray-300'
                                }`}
                              >
                                {sub.planName}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                            <span>
                              {t.total || 'Total'}:{' '}
                              <strong className={isAnnual ? 'text-gray-800' : 'text-gray-500'}>
                                {sub.purchasedLicenses ?? '-'}
                              </strong>{' '}
                              ({t.in_use || 'In use'}: {sub.assignedLicenses ?? '-'})
                            </span>
                            {sub.renewalDate && (
                              <span>
                                {t.renewal_date || 'Renewal Date'}:{' '}
                                <strong className={isAnnual ? 'text-gray-800' : 'text-gray-500'}>
                                  {sub.renewalDate}
                                </strong>
                              </span>
                            )}
                            {catalogPrice !== null && !isNaN(catalogPrice) && (
                              <span>
                                {t.price || 'Catalog Price'}:{' '}
                                <strong className={isAnnual ? 'text-primary' : 'text-gray-500'}>
                                  €{catalogPrice.toFixed(2)}
                                </strong>
                              </span>
                            )}
                          </div>
                        </div>

                        <button
                          type="button"
                          disabled={!isAnnual}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isAnnual) {
                              handleSelectSubscription(sub);
                            }
                          }}
                          className={`shrink-0 px-3 py-1.5 text-xs font-bold rounded-md transition-colors self-start sm:self-center ${
                            !isAnnual
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-primary text-white hover:bg-primary-hover'
                          }`}
                        >
                          {isSelectedInForm
                            ? t.selected || 'Selected'
                            : t.use_subscription || 'Select'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* 3. Line Items Configuration */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700">
                {t.order_items || '2. License / Product Details'} *
              </label>
              <button
                type="button"
                onClick={handleAddLineItem}
                className="text-xs font-bold text-primary hover:text-indigo-800 flex items-center gap-1"
              >
                + {t.add_new_item || 'Add another license'}
              </button>
            </div>

            {items.map((item, idx) => {
              const startDisplay = formatInputDateToDisplay(item.startDate);
              const endDisplay = formatInputDateToDisplay(item.endDate);
              const previewSubtitle =
                item.name && startDisplay && endDisplay
                  ? `${item.name} - ${startDisplay} - ${endDisplay}`
                  : '';

              return (
                <div
                  key={idx}
                  className={`space-y-4 relative ${idx > 0 ? 'pt-4 border-t border-gray-200' : ''}`}
                >
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveLineItem(idx)}
                      className="absolute top-0 right-0 text-red-400 hover:text-red-600 p-1"
                      title={t.remove || 'Remove'}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}

                  {/* Product Selector & Quantity */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="sm:col-span-3">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        {t.product || 'Catalog Product (Google License)'} *
                      </label>
                      <SearchableSelect
                        options={productOptions}
                        value={item.productId}
                        onChange={(val) => handleProductChange(idx, val)}
                        placeholder={t.search_workspace_plans || 'Choose Google Workspace license...'}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        {t.quantity || 'Quantity'} *
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        required
                        value={item.quantity}
                        onChange={(e) => handleItemFieldChange(idx, 'quantity', e.target.value)}
                        className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* Date Range (Start Date - End Date, +1 Year helper) */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        {t.license_start_date || 'Start Date'} *
                      </label>
                      <input
                        type="date"
                        required
                        value={item.startDate}
                        onChange={(e) => handleItemFieldChange(idx, 'startDate', e.target.value)}
                        className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-semibold text-gray-600">
                          {t.license_end_date || 'End Date (+1 Year)'} *
                        </label>
                        <button
                          type="button"
                          onClick={() =>
                            handleItemFieldChange(
                              idx,
                              'endDate',
                              addOneYearInputDate(item.startDate)
                            )
                          }
                          className="text-[11px] font-bold text-primary hover:underline"
                        >
                          +1 {t.year || 'Year'}
                        </button>
                      </div>
                      <input
                        type="date"
                        required
                        value={item.endDate}
                        onChange={(e) => handleItemFieldChange(idx, 'endDate', e.target.value)}
                        className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                      />
                    </div>

                    <div className="flex items-center gap-4 pb-2">
                      <label className="inline-flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={item.isRenewal}
                          onChange={(e) =>
                            handleItemFieldChange(idx, 'isRenewal', e.target.checked)
                          }
                          className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                        />
                        {t.renewal || 'Is Renewal'}
                      </label>
                      <span className="ml-auto text-sm font-bold text-gray-900">
                        €{((parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0)).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {previewSubtitle && (
                    <div className="text-xs text-gray-500 bg-gray-50 px-3 py-1.5 rounded border border-gray-200 font-mono">
                      {t.description || 'Invoice Line'}: {previewSubtitle}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 4. Order Metadata & Totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 border-t border-gray-200">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                {t.purchase_order || 'Purchase Order / Reference'}
              </label>
              <input
                type="text"
                value={purchaseOrder}
                onChange={(e) => setPurchaseOrder(e.target.value)}
                placeholder="Domain Reactivation"
                className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
              />

              <div className="mt-3.5 pt-3 border-t border-gray-200">
                <label className="flex items-start gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={skipResellerApi}
                    onChange={(e) => setSkipResellerApi(e.target.checked)}
                    className="mt-0.5 rounded text-primary focus:ring-primary h-4 w-4 border-gray-300"
                  />
                  <div>
                    <span className="text-xs font-semibold text-gray-700">
                      {t.skip_reseller_api_checkbox || 'Handmatige Google-verwerking (sla Google API over bij betaling)'}
                    </span>
                    <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">
                      {skipResellerApi
                        ? (t.skip_reseller_api_desc_checked || 'Google API wordt NIET aangeroepen wanneer de klant betaalt (handig als licenties al handmatig zijn heractiveerd).')
                        : (t.skip_reseller_api_desc_unchecked || 'Google API zal licenties automatisch heractiveren en verlengen zodra de klant betaalt.')}
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Totals Box */}
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 flex flex-col justify-between">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>{t.subtotal || 'Subtotal'}</span>
                  <span className="font-semibold text-gray-800">€ {subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>
                    {t.vat || 'VAT'} ({vatPercent}%)
                  </span>
                  <span className="font-semibold text-gray-800">€ {vatAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base font-bold text-gray-900 pt-2 border-t border-gray-300">
                  <span>{t.total_amount || 'Total Amount'}</span>
                  <span className="text-primary">€ {totalPrice.toFixed(2)}</span>
                </div>
              </div>
              <p className="text-[11px] text-gray-500 mt-3">
                {t.create_order_pay_hint ||
                  'Once created, the customer can immediately pay this order from their Order History page via Stripe.'}
              </p>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 text-sm font-semibold transition-colors"
          >
            {t.cancel || 'Cancel'}
          </button>
          <button
            type="submit"
            disabled={submitting || !selectedCustomerId}
            className="px-5 py-2 rounded-lg bg-primary text-white hover:bg-primary-hover disabled:opacity-50 text-sm font-bold shadow-sm transition-colors flex items-center gap-2"
          >
            {submitting ? (
              <>
                <svg
                  className="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t.saving || 'Creating...'}
              </>
            ) : (
              t.create_order || 'Create Order'
            )}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default CreateOrderModal;
